import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Heart, Menu, X, ChevronRight, UserPlus, PlusCircle, Users, CheckCircle2, BarChart3, HelpCircle, HandHeart } from "lucide-react";
import { ThemeToggle } from "../components/ThemeToggle";
import { PrayerCountExamples } from "../components/PrayerCountExamples";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const STEPS = [
  {
    num: "01",
    icon: UserPlus,
    title: "Create your account",
    body: "Sign up with just your name, email, and a password. No credit card, no setup fuss — you're ready to record your first prayer in under a minute.",
    image: "/how-it-works/01-signup.webp",
    alt: "Prayer Tracker signup screen with name, email, and password fields",
    testId: "step-signup",
  },
  {
    num: "02",
    icon: PlusCircle,
    title: "Record a prayer",
    body: "Write down what's on your heart. Add notes, tag it by category (Family, Health, Gratitude, or your own custom tags), and mention who asked for prayer. Your list stays private to you by default.",
    image: "/how-it-works/03-new-prayer.webp",
    alt: "New prayer dialog with a sample prayer request and tags selected",
    testId: "step-add-prayer",
  },
  {
    num: "03",
    icon: Users,
    title: "Create or join a group",
    body: "Share prayers with your small group, family, or church by creating a group. Invite others with a shareable code — everyone in the group sees the same prayer list and can update it together.",
    image: "/how-it-works/04-groups.webp",
    alt: "Dashboard showing the My Groups section with a Sunday Small Group and an invite code",
    testId: "step-groups",
  },
  {
    num: "04",
    icon: HandHeart,
    title: 'Pray for one another',
    body: 'Tap the menu on any active prayer and choose "Prayed for" — the count next to that request bumps up by one. There\'s no limit and no judgment: pray for the same request every morning, every commute, every breath. The counter quietly tracks how many prayers your community has lifted up, growing the visible support around each request without ever pinging the requester.',
    image: "/how-it-works/07-prayed-for-menu.jpg",
    alt: 'Dashboard with the Prayed-for menu open on a prayer card, showing the small hand-heart counter chip "2" next to the request date',
    testId: "step-prayed-for",
    extra: "examples",
  },
  {
    num: "05",
    icon: CheckCircle2,
    title: "Celebrate answered prayers",
    body: "When God answers, tap Mark as Answered, write a short story of how it was answered, and save it. Your celebration is recorded forever — a living record of faithfulness.",
    image: "/how-it-works/06-mark-answered.webp",
    alt: "Mark answered dialog where the user describes how the prayer was answered",
    testId: "step-answered",
  },
  {
    num: "06",
    icon: BarChart3,
    title: "See your journey at a glance",
    body: "Your dashboard shows your personal prayers and the prayers from any group you are a member of. It also shows your trends over time, answer rates, and who's been most on your heart. Filter by tag or date range, print a list for your prayer closet, or share an answered prayer with your group.",
    image: "/how-it-works/05-analytics.webp",
    alt: "Analytics dashboard showing prayers over time, category distribution, and top requesters",
    testId: "step-analytics",
  },
];

const FAQS = [
  {
    q: "What does the 'Prayed for' option do?",
    a: 'Tapping "Prayed for" in a prayer\'s menu adds one to the small counter shown next to that prayer. It\'s an encouragement tool, not a tally of unique pray-ers — every tap counts, so you can pray for the same request as often as you like. The counter stays hidden when it\'s still at zero and quietly grows as your community lifts the request up.',
  },
  {
    q: "Can the requester see when I pray for them?",
    a: "Not directly. The counter is the only signal, and it doesn't reveal who tapped it — only how often the request has been prayed for. This keeps the focus on the request itself, not on social pressure or tracking individual responses.",
  },
  {
    q: "Is my data private?",
    a: "Yes. Every prayer you record stays private by default. You choose when to share with a group, and you can hide, edit, or delete any prayer at any time. We never sell your data or use it for advertising.",
  },
  {
    q: "Is it really free?",
    a: "The core app is free to use — unlimited prayers, unlimited groups, printable lists, and dark mode are all included. If you'd like to support ongoing development, you can make a one-time donation, but there's no paywall and no feature is locked.",
  },
  {
    q: "How do prayer groups work?",
    a: "Create a group and share the invite code with your small group, family, or church. Everyone who joins sees the same list of prayer requests and can add new ones, mark prayers as answered, or leave private notes. You can belong to as many groups as you'd like.",
  },
  {
    q: "Can I print my prayers?",
    a: "Absolutely. Every prayer list has a Print button that opens a print-optimised layout — perfect for carrying in your Bible, handing out at a small group, or keeping in your prayer closet.",
  },
  {
    q: "What if a prayer is deeply personal?",
    a: "Keep it in your personal list (the default). It's visible only to you. You can also hide any prayer — group or personal — so it's kept in the record but not shown in the main view.",
  },
  {
    q: "Is there a mobile app?",
    a: "The web app works beautifully on phones and tablets, and we also have a native iOS + Android app in the works. You'll be able to use the same account on every device.",
  },
];

export default function HowItWorksPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": FAQS.map(({ q, a }) => ({
      "@type": "Question",
      "name": q,
      "acceptedAnswer": { "@type": "Answer", "text": a },
    })),
  };

  // SAFE: FAQS is a compile-time constant (no user input reaches this string).
  // dangerouslySetInnerHTML is the only way to inject JSON-LD in React — regular
  // JSX children of <script> get HTML-escaped, which breaks JSON parsing.
  // We still escape "<" to prevent any future "</script>" break-out if FAQ text
  // ever accidentally contains that sequence (standard Next.js / Helmet pattern).
  const jsonLdSafe = JSON.stringify(faqJsonLd).replace(/</g, "\\u003c");

  return (
    <div className="min-h-screen bg-stone-light noise-bg dark:bg-stone-dark" data-testid="how-it-works-page">
      <script
        type="application/ld+json"
        data-testid="faq-jsonld"
        dangerouslySetInnerHTML={{ __html: jsonLdSafe }}
      />
      {/* Navigation — mirrors LandingPage */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-4 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-full px-6 py-3 nav-shadow flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-lg text-stone-dark dark:text-stone-light">Prayer Tracker</span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-stone-dark/80 dark:text-stone-light/80 hover:text-sage transition-colors font-medium" data-testid="nav-home">Home</Link>
              <Link to="/how-it-works" className="text-sage dark:text-sage-light transition-colors font-semibold" data-testid="nav-how-it-works-active">How It Works</Link>
              <Link to="/donate" className="text-stone-dark/80 dark:text-stone-light/80 hover:text-sage transition-colors font-medium" data-testid="nav-donate">Donate</Link>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-sage hover:bg-sage/10 font-medium">Log In</Button>
              </Link>
              <Link to="/signup" data-testid="nav-signup">
                <Button className="bg-sage hover:bg-sage-dark text-white rounded-full px-6 btn-glow">Get Started</Button>
              </Link>
              <ThemeToggle />
            </div>

            <button className="md:hidden p-2 hover:bg-stone/50 rounded-full transition-colors" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-5 h-5 text-stone-dark dark:text-stone-light" /> : <Menu className="w-5 h-5 text-stone-dark dark:text-stone-light" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden glass rounded-2xl mt-2 p-4 nav-shadow animate-fade-in">
              <div className="flex flex-col gap-2">
                <Link to="/" className="px-4 py-2 text-stone-dark/80 dark:text-stone-light/80 hover:bg-stone/50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Home</Link>
                <Link to="/how-it-works" className="px-4 py-2 text-sage font-semibold hover:bg-stone/50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>How It Works</Link>
                <Link to="/donate" className="px-4 py-2 text-stone-dark/80 dark:text-stone-light/80 hover:bg-stone/50 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Donate</Link>
                <Link to="/login" className="px-4 py-2 text-sage hover:bg-sage/10 rounded-lg" onClick={() => setMobileMenuOpen(false)}>Log In</Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-sage hover:bg-sage-dark text-white rounded-full mt-2">Get Started</Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero intro */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-gold/10 text-gold-dark px-4 py-2 rounded-full mb-6 animate-fade-in">
            <Heart className="w-4 h-4" />
            <span className="text-sm font-medium">A quick tour</span>
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-stone-dark dark:text-stone-light leading-tight mb-6 animate-fade-in stagger-1" data-testid="howitworks-hero-headline">
            How <span className="text-sage">Prayer Tracker</span> works
          </h1>
          <p className="text-lg text-stone-dark/70 dark:text-stone-light/70 animate-fade-in stagger-2" data-testid="howitworks-hero-subheadline">
            Five simple steps from signing up to celebrating answered prayers. Real screenshots, real flow — no surprises.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto space-y-24 md:space-y-32">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const reverse = idx % 2 === 1;
            return (
              <div
                key={step.num}
                data-testid={step.testId}
                className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                {/* Text */}
                <div className="text-left">
                  <div className="flex items-center gap-4 mb-4">
                    <span className="font-serif text-5xl font-semibold text-sage/30 dark:text-sage-light/30 leading-none">{step.num}</span>
                    <div className="w-10 h-10 rounded-full bg-sage/10 dark:bg-sage/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-sage dark:text-sage-light" />
                    </div>
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-stone-dark dark:text-stone-light mb-4">{step.title}</h2>
                  <p className="text-base md:text-lg text-stone-dark/70 dark:text-stone-light/70 leading-relaxed">{step.body}</p>
                  {step.extra === "examples" && <PrayerCountExamples />}
                </div>

                {/* Screenshot */}
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-sage/20 via-gold/10 to-stone/20 rounded-3xl blur-2xl -z-10" />
                  <div className="rounded-2xl overflow-hidden shadow-xl border border-stone/50 dark:border-stone-dark/40 bg-white">
                    <img
                      src={step.image}
                      alt={step.alt}
                      loading="lazy"
                      className="w-full h-auto block"
                      data-testid={`${step.testId}-image`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-white dark:bg-stone-dark/40" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-sage/10 text-sage dark:bg-sage/20 px-4 py-2 rounded-full mb-5">
              <HelpCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Common questions</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl font-semibold text-stone-dark dark:text-stone-light mb-3" data-testid="faq-headline">
              Frequently asked
            </h2>
            <p className="text-base md:text-lg text-stone-dark/70 dark:text-stone-light/70">
              Quick answers to the things most people wonder about.
            </p>
          </div>
          <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
            {FAQS.map((item, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="border-b border-stone/70 dark:border-stone-dark/60"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger
                  className="text-left py-5 text-base md:text-lg font-medium text-stone-dark dark:text-stone-light hover:text-sage dark:hover:text-sage-light hover:no-underline"
                  data-testid={`faq-trigger-${i}`}
                >
                  {item.q}
                </AccordionTrigger>
                <AccordionContent
                  className="text-stone-dark/70 dark:text-stone-light/70 text-base leading-relaxed pb-5"
                  data-testid={`faq-content-${i}`}
                >
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className="text-sm text-stone-dark/60 dark:text-stone-light/60 text-center mt-10">
            Still have a question?{" "}
            <Link to="/donate" className="text-sage hover:text-sage-dark underline underline-offset-2">
              Reach out on the Donate page
            </Link>{" "}
            — we read every message.
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-4 bg-sage text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-semibold mb-4">Ready to start your prayer journal?</h2>
          <p className="text-lg text-white/80 mb-8">Join thousands of believers recording, tracking, and celebrating answered prayer.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" data-testid="howitworks-cta-signup">
              <Button size="lg" className="bg-white hover:bg-stone-light text-sage-dark rounded-full px-8 py-6 text-base font-semibold w-full sm:w-auto">
                Create a free account
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/" data-testid="howitworks-cta-home">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-sage-dark rounded-full px-8 py-6 text-base font-medium w-full sm:w-auto">
                Back to home
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="py-8 text-center text-sm text-stone-dark/60 dark:text-stone-light/60">
        © 2026 Prayer Tracker. Made with <Heart className="w-3 h-3 inline text-sage" /> for those who pray.
      </footer>
    </div>
  );
}
